import {useEffect, useState} from 'react'
import {usePrevious} from '@eitje/hooks'
import {makeNewRegisteredField, useForm, useFormData} from '@eitje/form'
import {t} from '@eitje/bridge'
import utils from '@eitje/utils'
import _ from 'lodash'
import pluralize from 'pluralize'
import {Icon, EitjeButton, Layout, Text} from '~/components'
import {FormRow} from '../components'

const FIELD_DELIMITER = `--zz--`

const buildItem = (field, item) => {
	const id = item?.id || utils.randomId()
	return {id, isNew: !item?.id, baseField: `arrayField${FIELD_DELIMITER}${field}-${id}`, ...item}
}

const getField = (item, key) => {
	const {baseField} = item
	return `${baseField}-${key}`
}

const getInitial = (data, field, minItems, initialItems) => {
	const initialCount = Math.max(minItems, initialItems)

	if (data[field]) {
		const extraItems = Math.max(0, initialCount - data[field].length)
		return [...data[field], ...Array(extraItems)]
	}
	return [...Array(initialCount)]
}

// get a change key for object values, otherwise just return the value
const getChangeKey = value => {
	if (_.isPlainObject(value)) return JSON.stringify(value)
	return value
}

let ArrayField = ({minItems = 1, initialItems = minItems, maxItems = 100, fields = [], onRemove = _.noop, field, readOnly, ...rest}) => {
	const fieldNames = fields._map('field')
	const data = useFormData()
	const {setValues} = useForm()
	const initialArrayItems = getInitial(data, field, minItems, initialItems).map(item => buildItem(field, item))
	const [items, setItems] = useState(initialArrayItems)
	const fieldKeyStart = `arrayField${FIELD_DELIMITER}${field}-`
	const relevantData = _.pickBy(data, (val, key) => key.startsWith(fieldKeyStart) && val != null && val != undefined)
	const joined = Object.values(relevantData).map(getChangeKey).join('')
	const previousJoined = usePrevious(joined)

	useEffect(() => {
		if (data[field]) {
			const initialObject = {}
			fieldNames.forEach(fieldName =>
				items.forEach(item => {
					const key = getField(item, fieldName)
					initialObject[key] = item[fieldName]
				}),
			)

			setValues(initialObject)
		}
	}, [])

	useEffect(() => {
		if (joined == '' && !previousJoined) return // we want to not run this initially, but want to run it later on if joined becomes ''
		const newVals = items
			.map(item => {
				const obj = {id: item.id}
				fieldNames.forEach(fieldName => {
					obj[fieldName] = data[getField(item, fieldName)]
				})
				return obj
			})
			.filter(obj => Object.values(obj).filter(Boolean).length > 1)
		rest.onChange(newVals)
	}, [joined, items])

	const addItem = () => setItems(items => [...items, buildItem(field)])

	const removeItem = item => {
		if (items.length <= minItems) {
			// Clear the values of the item
			const clearedValues = {}
			fieldNames.forEach(fieldName => {
				clearedValues[getField(item, fieldName)] = null
			})
			setValues(clearedValues)
		} else {
			// Remove the item from the array
			setItems(items => items.filter(i => i.id !== item.id))
		}

		return onRemove(item.id)
	}

	const allowRemove = minItems < items.length
	const showAddItemRow = maxItems > items.length && !readOnly
	return (
		<>
			{items.map((i, idx) => (
				<ArrayRow
					{...rest}
					item={i}
					idx={idx}
					fields={fields}
					translationField={pluralize.singular(field)}
					allowRemove={allowRemove}
					remove={e => {
						e.stopPropagation()
						removeItem(i)
					}}
				/>
			))}
			{showAddItemRow && <AddItemRow {...rest} onClick={addItem} />}
		</>
	)
}

const ArrayRowDeleteButton = props => {
	return <EitjeButton className="delete-row-button" iconLeft="trash" baseColor="red" {...props} dropdown={false} />
}

const ArrayRow = ({id, idx, translationField, remove, fields = [], item, allowRemove, formName}) => {
	return (
		<FormRow>
			{fields.map((f, fieldIdx) => (
				<Field
					item={item}
					field={f}
					idx={idx}
					id={id}
					remove={remove}
					allowRemove={allowRemove && fieldIdx == fields.length - 1}
					parentField={translationField}
					name={formName}
				/>
			))}
		</FormRow>
	)
}

const Field = ({idx, field, item, name, parentField, allowRemove, remove}) => {
	const {Component, addCountToLabel, label: customLabel, ...rest} = field
	let label = customLabel ?? t(`form.${name}.fields.${parentField}.${field.field}.label`)
	if (addCountToLabel) label = `${label} ${idx + 1}`
	return (
		<Component
			{...rest}
			field={`${item.baseField}-${field.field}`}
			label={label}
			rightElement={allowRemove && <ArrayRowDeleteButton onClick={remove} />}
			clearIcon={false}
		/>
	)
}

export const AddItemRow = ({onClick, iconLeft = 'plus', addItemLabel = 'add row', disabled}) => {
	return (
		<Layout className="add-row-button" block width="full">
			<Layout horizontal="center" width="full" height={40} onClick={onClick} colorSet="color-bordered-fill" gap={6} disabled={disabled}>
				<Icon name={iconLeft} size={10} />
				<Text small t={addItemLabel} />
			</Layout>
		</Layout>
	)
}

ArrayField = makeNewRegisteredField(ArrayField, {})
export {ArrayField}
